import React, {useEffect} from "react";
import { useRouter } from 'next/router';
import { makeStyles } from 'tss-react/mui';

// core components
import Footer from "../../../components/Footer/Footer.js";
import Header from "../../../components/Header/Header.js";
import HeaderLinks from "../../../components/Header/HeaderLinks.js";
import logo from "../../../public/img/logo.png";
import Parallax from "../../../components/Parallax/Parallax";

import styles from "../../../styles/jss/nextjs-material-kit/pages/jobAndCoursesStyle.js";

// Sections for this page
import PreFooter from "../../../pages-sections/LandingPage-Sections/PreFooter.js";
import OrganisationDetails from "../../../pages-sections/LandingPage-Sections/OrganisationDetails";

const dashboardRoutes = [];
const useStyles = makeStyles()(styles);

import {useEmployersTrackRequest} from "../../../request/api";
import {client} from "../../../request/client";
import useSWR, {SWRConfig} from "swr";
import Head from "next/head";
import {buildSlug} from "../../../util/url";
import JSONSchemaData from "../../../components/JSONSchemaData/JSONSchemaData";

function EmployerPage(props) {
  const { classes, cx } = useStyles();
  const router = useRouter();
  const { employerId, ...rest } = props;

  const { data: employer, error, isLoading } = useSWR(`/v1/employers/${employerId}`);
  const { data: forumAndApprentices } = useSWR(`/v1/employer/${employerId}/forum-categories`);
  const { trigger: trackEmployer } = useEmployersTrackRequest({ employerId });
  const apprentices = forumAndApprentices?.apprentices ?? [];
  const forumCategoryId = forumAndApprentices?.forum_category_id ?? null;
  const forumCategoryActive = forumAndApprentices?.forum_category_active ?? false;
  const orgDetails = {apprentices, forumCategoryId, forumCategoryActive};

  useEffect(() => {
    trackEmployer();
  }, []);


  const jsonBreadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://successatschool.org/"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Employers",
      "item": "https://successatschool.org/employers"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": employer?.name,
      "item": `https://successatschool.org/employer/${buildSlug(employer?.name)}/${employer?.id}`
    }]};

  return (
    <div>
      <Header
        color="transparent"
        routes={dashboardRoutes}
        logo={logo}
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{
          height: 400,
          color: "white",
        }}
        {...rest}
      />
      <Head>
        {!!employer && <>
          <link rel="canonical" href={`https://successatschool.org/employer/${buildSlug(employer.name)}/${employer.id}`} />
          <title>{`${employer?.name} - Success at School`}</title>
        </>}
      </Head>
      <Parallax className="parallaxx-compact" filter image={employer.header_uri || "/img/faces/employers_img.png"} />

      {employer && <OrganisationDetails org={employer} orgDetails={orgDetails}/>}

      <div className={cx(classes.darkBlue, "footer", "colored_bgg")}>
        <PreFooter />
      </div>
      <Footer />
      <JSONSchemaData key_name={'breadcrumbSchema'} data={jsonBreadcrumbData} />
    </div>
  );
}

export default function EmployerPageSwr({ fallback, ...rest }) {
  return (
      <SWRConfig value={{ fallback }}>
        <EmployerPage {...rest} />
      </SWRConfig>
  );
}


export async function getServerSideProps({ query }) {
  const { employerId } = query;

  try {
    const [employerData, forumAndApprentices] = await Promise.all([
      client({
        url: `/v1/employers/${employerId}`,
        method: "get",
        headers: {},
      }).then((res) => res.data),
      client({
        url: `/v1/employer/${employerId}/forum-categories`,
        method: "get",
        headers: {},
      }).then((res) => res.data)
    ]);

    return {
      props: {
        fallback: {
          [`/v1/employers/${employerId}`]: employerData,
          [`/v1/employer/${employerId}/forum-categories`]: forumAndApprentices,
        },
        employerId,
      }
    };
  }
  catch (e) {
    if (e.response && e.response.status === 404) {
      return {
        notFound: true,
      };
    }
    else {
      throw e;
    }
  }
}
